import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';

const SmartVideoEmbed = ({ url, width = '100%', height = '400px' }) => {
  const [error, setError] = useState(null);

  const getVideoType = (url) => {
    if (!url) return null;

    const lower = url.toLowerCase();

    if (/youtu\.be|youtube\.com/.test(lower)) return 'youtube';
    if (/vimeo\.com/.test(lower)) return 'vimeo';
    if (/wistia\.com/.test(lower)) return 'wistia';

    return null;
  };

  const extractVideoId = (url, type) => {
    if (!url) return null;

    switch (type) {
      case 'youtube':
        if (url.length === 11) return url;
        const ytPatterns = [
          /youtu\.be\/([^?\/]+)/,
          /youtube\.com\/watch\?v=([^&]+)/,
          /youtube\.com\/embed\/([^?\/]+)/,
          /youtube\.com\/v\/([^?\/]+)/,
          /youtube\.com\/shorts\/([^?\/]+)/
        ];
        for (const pattern of ytPatterns) {
          const match = url.match(pattern);
          if (match) return match[1];
        }
        return url;

      case 'vimeo':
        const match = url.match(/(?:vimeo\.com\/)([^"&?\/\s]+)/);
        return match ? match[1] : null;

      case 'wistia':
        const wistiaMatch = url.match(/(?:wistia\.com\/medias\/)([^"&?\/\s]+)/);
        return wistiaMatch ? wistiaMatch[1] : null;

      default:
        return null;
    }
  };

  const videoType = getVideoType(url);
  const videoId = extractVideoId(url, videoType);

  useEffect(() => {
    if (!videoId) {
      setError(`Invalid ${videoType} video ID or URL`);
    }
  }, [videoId, videoType]);

  if (!videoType) {
    return (
      <div className="flex items-center justify-center p-6 bg-gray-800 rounded-lg">
        <p className="text-red-400">Unsupported video URL</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center p-6 bg-gray-800 rounded-lg">
        <p className="text-red-400">{error}</p>
      </div>
    );
  }

  const getEmbedUrl = () => {
    switch (videoType) {
      case 'youtube':
        return `https://www.youtube.com/embed/${videoId}?rel=0`;
      case 'vimeo':
        return `https://player.vimeo.com/video/${videoId}`;
      case 'wistia':
        return `https://fast.wistia.net/embed/iframe/${videoId}`;
      default:
        return '';
    }
  };

  return (
    <iframe
        src={getEmbedUrl()}
        className="top-0 left-0 w-full"
        width={width}
        height={height}
        frameBorder="0"
        allow={
          videoType === 'youtube'
            ? 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture'
            : 'autoplay; fullscreen; picture-in-picture'
        }
        allowFullScreen
        onError={() => setError(`Failed to load ${videoType} video`)}
      />
  );
};

SmartVideoEmbed.propTypes = {
  url: PropTypes.string.isRequired,
  width: PropTypes.string,
  height: PropTypes.string,
};

export default SmartVideoEmbed;
